import { useForm, FormProvider, Controller } from "react-hook-form";
import {
  Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid, TextField, FormControl, InputLabel, Select, MenuItem
} from "@mui/material";
import { useMutation, useQueryClient } from "react-query";
import axiosClient from "../../../../../../../../lib/axios";
import { enqueueSnackbar } from "notistack";
import { useMemo } from "react";
import PaperDraggable from "../../../../../../../../components/PaperDraggable";
import { formatarCPFCNPJ, formatarTelefone } from "../../../../../../../../helpers/masks";
import { tiposRelacionamento } from "./helpers";

interface FormData {
  nome: string;
  documento: string;
  tipo_relacionamento: string;
  telefone: string;
}

interface DialogEditarCompradorAutorizadoProps {
  open: boolean;
  onClose: () => void;
  data: any;
  cooperadoId: string;
}


export default function DialogEditarCompradorAutorizado({
  open,
  onClose,
  data,
  cooperadoId
}: DialogEditarCompradorAutorizadoProps) {
  const queryClient = useQueryClient();

  const defaultValues = useMemo(() => ({
    nome: data?.nome || "",
    documento: data?.documento || "",
    tipo_relacionamento: data?.tipo_relacionamento || "",
    telefone: data?.telefone || "",
  }), [data]);

  const methods = useForm<FormData>({
    defaultValues,
    values: defaultValues
  });

  const { control, handleSubmit } = methods;

  const { mutate: editarRelacionamento } = useMutation({
    mutationKey: ["editar-relacionamento", cooperadoId, data?.id],
    mutationFn: (formData: FormData) =>
      axiosClient.put(`cadastros/editar-comprador`, {
        ...formData,
        id: data?.id,
        cooperadoId: cooperadoId
      }),
    onError: () => {
      enqueueSnackbar("Erro ao editar comprador autorizado!", { variant: "error" });
    },
    onSuccess: () => {
      queryClient.invalidateQueries('cadastros/detalhar-cooperado')
      enqueueSnackbar("Relacionamento editado com sucesso!", {
        variant: "success",
      });
      onClose();
    },
  });

  const handleFormSubmit = (formData: FormData) => {
    if (!formData.nome.trim()) {
      enqueueSnackbar("Nome é obrigatório.", { variant: "warning" });
      return;
    }

    editarRelacionamento(formData);
  };

  return (
    <Dialog
      open={open}
      fullWidth
      maxWidth="md"
      PaperComponent={PaperDraggable}
      aria-labelledby="draggable-dialog-title"
    >
      <DialogTitle style={{ cursor: 'move' }} id="draggable-dialog-title">
        Editar Comprador Autorizado
      </DialogTitle>
      <DialogContent>
        <FormProvider {...methods}>
          <form id="editar-relacionamento-form" onSubmit={handleSubmit(handleFormSubmit)}>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="nome"
                  control={control}
                  rules={{ required: "Campo obrigatório" }}
                  render={({ field, fieldState: { error } }) => (
                    <TextField
                      {...field}
                      label="Nome"
                      fullWidth
                      error={!!error}
                      helperText={error?.message}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="documento"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Documento"
                      fullWidth
                      onChange={(e) => {
                        const value = formatarCPFCNPJ(e.target.value);
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="telefone"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      label="Telefone"
                      fullWidth
                      onChange={(e) => {
                        const value = formatarTelefone(e.target.value);
                        field.onChange(value);
                      }}
                    />
                  )}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 4 }}>
                <Controller
                  name="tipo_relacionamento"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Tipo Relacionamento</InputLabel>
                      <Select
                        {...field}
                        label="Tipo Relacionamento"
                      >
                        {tiposRelacionamento.map((tipo) => (
                          <MenuItem key={tipo} value={tipo}>
                            {tipo}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>


            </Grid>
          </form>
        </FormProvider>
      </DialogContent>
      <DialogActions>
        <Button variant="outlined" onClick={onClose}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          type="submit"
          form="editar-relacionamento-form"
        >
          Salvar
        </Button>
      </DialogActions>
    </Dialog>
  );
}